import React, { useEffect, useRef, useState } from 'react'

interface PerformanceMetrics {
  renderTime: number
  memoryUsage?: number
  domNodes: number
  scrollPerformance: number
}

interface PerformanceMonitorProps {
  enabled?: boolean
  onMetricsChange?: (metrics: PerformanceMetrics) => void
  className?: string
  style?: React.CSSProperties
}

const PerformanceMonitor: React.FC<PerformanceMonitorProps> = ({
  enabled = false,
  onMetricsChange,
  className = '',
  style = {},
}) => {
  const [metrics, setMetrics] = useState<PerformanceMetrics>({
    renderTime: 0,
    memoryUsage: 0,
    domNodes: 0,
    scrollPerformance: 0,
  })
  
  const renderStartTime = useRef<number>(0)
  const scrollStartTime = useRef<number>(0)
  const frameCount = useRef<number>(0)
  const lastFrameTime = useRef<number>(0)

  // 测量渲染性能
  useEffect(() => {
    if (!enabled) return

    renderStartTime.current = performance.now()
    
    const measureRender = () => {
      const renderTime = performance.now() - renderStartTime.current
      const domNodes = document.querySelectorAll('*').length
      
      // 获取内存使用情况（仅在支持的环境中）
      let memoryUsage = 0
      if ('memory' in performance) {
        memoryUsage = (performance as any).memory.usedJSHeapSize / 1024 / 1024 // MB
      }
      
      setMetrics(prev => ({
        ...prev,
        renderTime,
        memoryUsage,
        domNodes,
      }))
      
      if (onMetricsChange) {
        onMetricsChange({
          renderTime,
          memoryUsage,
          domNodes,
          scrollPerformance: prev.scrollPerformance,
        })
      }
    }

    // 使用requestAnimationFrame确保在下一帧测量
    requestAnimationFrame(measureRender)
  }, [enabled, onMetricsChange])

  // 测量滚动性能
  useEffect(() => {
    if (!enabled) return

    const handleScroll = () => {
      const now = performance.now()
      frameCount.current++
      
      if (lastFrameTime.current === 0) {
        lastFrameTime.current = now
        return
      }
      
      const frameTime = now - lastFrameTime.current
      const fps = 1000 / frameTime
      
      setMetrics(prev => ({
        ...prev,
        scrollPerformance: fps,
      }))
      
      lastFrameTime.current = now
    }

    // 监听滚动事件
    const elements = document.querySelectorAll('.virtual-list, .results-list, .selected-list')
    elements.forEach(element => {
      element.addEventListener('scroll', handleScroll, { passive: true })
    })

    return () => {
      elements.forEach(element => {
        element.removeEventListener('scroll', handleScroll)
      })
    }
  }, [enabled])

  if (!enabled) return null

  return (
    <div 
      className={`performance-monitor ${className}`}
      style={{
        position: 'fixed',
        top: '10px',
        right: '10px',
        background: 'rgba(0, 0, 0, 0.8)',
        color: '#fff',
        padding: '8px 12px',
        borderRadius: '4px',
        fontSize: '12px',
        fontFamily: 'monospace',
        zIndex: 9999,
        minWidth: '200px',
        ...style,
      }}
    >
      <div style={{ marginBottom: '4px', fontWeight: 'bold' }}>性能监控</div>
      <div>渲染时间: {metrics.renderTime.toFixed(2)}ms</div>
      <div>DOM节点: {metrics.domNodes.toLocaleString()}</div>
      {metrics.memoryUsage > 0 && (
        <div>内存使用: {metrics.memoryUsage.toFixed(2)}MB</div>
      )}
      <div>滚动FPS: {metrics.scrollPerformance.toFixed(1)}</div>
      
      {/* 性能状态指示器 */}
      <div style={{ marginTop: '8px', paddingTop: '8px', borderTop: '1px solid rgba(255, 255, 255, 0.3)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
          <span>状态:</span>
          <span style={{
            color: metrics.renderTime < 16 ? '#52c41a' : 
                   metrics.renderTime < 33 ? '#faad14' : '#ff4d4f',
            fontWeight: 'bold'
          }}>
            {metrics.renderTime < 16 ? '优秀' : 
             metrics.renderTime < 33 ? '良好' : '需优化'}
          </span>
        </div>
      </div>
    </div>
  )
}

export default PerformanceMonitor
